import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, Shield, Users, TrendingDown, Calendar, FileCheck, Eye, MapPin } from "lucide-react";
import { useT } from "../i18n/I18nContext";
import { InterestForm } from "../components/nivara/InterestForm";

const HERO_BG = "";  // Replaced with CSS gradient for a calmer, modern feel
const SWISS_IMG = "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";  // Warm community gathering (friends at wooden table)
const ZURICH_IMG = "https://images.unsplash.com/photo-1543269865-cbf427effbad?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";  // Warm friends gathering — conversation, casual community

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E2E8E4] bg-white text-[10px] sm:text-xs font-semibold tracking-[0.12em] uppercase text-[#475569]">
      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
      {children}
    </span>
  );
}

export default function LandingPage() {
  const t = useT();

  // React Router's <Link to="/#interest"> changes URL but doesn't trigger
  // native fragment scroll. Honor any hash present on mount.
  useEffect(() => {
    if (window.location.hash === "#interest") {
      requestAnimationFrame(() => {
        document.getElementById("interest")?.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E2E8E4]">
        {/* Modern, calm background: soft radial gradients + subtle noise */}
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(1200px 600px at 85% -10%, rgba(212,175,55,0.10), transparent 60%), " +
                "radial-gradient(900px 500px at -10% 80%, rgba(15,76,58,0.07), transparent 65%), " +
                "linear-gradient(180deg, #FAFBFA 0%, #FFFFFF 60%)",
            }}
          />
          {/* Soft ambient blob */}
          <div
            className="absolute -top-32 right-[-12%] w-[640px] h-[640px] rounded-full opacity-[0.18] blur-3xl"
            style={{ background: "radial-gradient(circle at 30% 30%, #D4AF37 0%, transparent 65%)" }}
          />
          <div
            className="absolute bottom-[-20%] left-[-10%] w-[720px] h-[720px] rounded-full opacity-[0.10] blur-3xl"
            style={{ background: "radial-gradient(circle at 50% 50%, #0F4C3A 0%, transparent 70%)" }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-28 pb-20 md:pb-32">
          <div className="max-w-3xl">
            <Pill>{t("brand.tagline")}</Pill>
            <h1 className="font-heading mt-8 text-4xl sm:text-5xl lg:text-7xl font-medium tracking-tight text-[#0F4C3A] leading-[1.02]">
              {t("landing.hero.headline_1")}
              <br />
              <span className="italic font-light text-[#475569]">{t("landing.hero.headline_2")}</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#475569] leading-relaxed max-w-2xl">
              {t("landing.hero.subtitle")}
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <a
                href="#interest"
                data-testid="hero-register-btn"
                className="nv-btn-primary px-8 py-4 rounded-md font-medium flex items-center justify-center gap-2 group"
              >
                {t("interest.cta_anchor")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/login"
                data-testid="hero-login-btn"
                className="px-8 py-4 rounded-md font-medium border border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#F8FAF9] transition text-center"
              >
                {t("landing.hero.cta_login")}
              </Link>
            </div>
            {/* Reduced metrics: only 2 most impactful */}
            <div className="mt-16 flex flex-wrap gap-12 max-w-xl">
              <div>
                <div className="font-mono-nv text-3xl md:text-4xl font-semibold text-[#0F4C3A]">0%</div>
                <div className="nv-label mt-2">{t("landing.stats.interest")}</div>
              </div>
              <div>
                <div className="font-mono-nv text-3xl md:text-4xl font-semibold text-[#0F4C3A]">100%</div>
                <div className="nv-label mt-2">{t("landing.stats.transparent")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Locations bar */}
      <section className="bg-[#0F4C3A] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 flex items-center justify-center gap-6 flex-wrap">
          <MapPin className="w-4 h-4 text-[#D4AF37]" />
          <div className="flex items-center gap-4 md:gap-8 flex-wrap justify-center">
            {["St. Gallen", "Zürich", "Bern", "Genf"].map((city, i) => (
              <div key={city} className="flex items-center gap-4 md:gap-8">
                <span className="font-heading text-base md:text-lg tracking-wide">{city}</span>
                {i < 3 && <span className="hidden md:block w-1 h-1 rounded-full bg-[#D4AF37]" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wie es funktioniert */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <Pill>{t("landing.how.label")}</Pill>
            <h2 className="font-heading mt-6 text-3xl md:text-5xl font-medium tracking-tight text-[#0F4C3A]">
              {t("landing.how.title")}
            </h2>
            <p className="mt-6 text-[#475569] leading-relaxed text-base md:text-lg">
              {t("landing.how.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-0 border border-[#E2E8E4] rounded-md overflow-hidden">
            {[
              { icon: Users, title: t("landing.how.f1.title"), text: t("landing.how.f1.text") },
              { icon: TrendingDown, title: t("landing.how.f2.title"), text: t("landing.how.f2.text") },
              { icon: Calendar, title: t("landing.how.f3.title"), text: t("landing.how.f3.text") },
            ].map((f, i) => (
              <div key={i} className="flex gap-5 p-6 border-b border-[#E2E8E4] last:border-b-0 hover:bg-[#F8FAF9] transition">
                <div className="flex-shrink-0 w-11 h-11 rounded-md bg-[#0F4C3A] flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="font-heading text-lg font-medium text-[#0F4C3A]">{f.title}</div>
                  <div className="text-sm text-[#475569] mt-1">{f.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / Swiss */}
      <section className="bg-[#F8FAF9] border-y border-[#E2E8E4]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div
              className="h-80 md:h-[500px] rounded-md bg-cover bg-center order-2 lg:order-1"
              style={{ backgroundImage: `url(${SWISS_IMG})` }}
            />
            <div className="order-1 lg:order-2">
              <Pill>{t("landing.trust.label")}</Pill>
              <h2 className="font-heading mt-6 text-3xl md:text-5xl font-medium tracking-tight text-[#0F4C3A]">
                {t("landing.trust.title")}
              </h2>
              <p className="mt-6 text-[#475569] leading-relaxed text-lg">
                {t("landing.trust.subtitle")}
              </p>
              <div className="mt-8 space-y-4">
                {[
                  { icon: Shield, title: t("landing.trust.p1.title"), text: t("landing.trust.p1.text") },
                  { icon: Eye, title: t("landing.trust.p2.title"), text: t("landing.trust.p2.text") },
                  { icon: FileCheck, title: t("landing.trust.p3.title"), text: t("landing.trust.p3.text") },
                ].map((p, i) => (
                  <div key={i} className="flex gap-4">
                    <p.icon className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-[#0F4C3A]">{p.title}</div>
                      <div className="text-sm text-[#475569]">{p.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bidding explainer */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Pill>{t("landing.bid.label")}</Pill>
            <h2 className="font-heading mt-6 text-3xl md:text-5xl font-medium tracking-tight text-[#0F4C3A]">
              {t("landing.bid.title")}
            </h2>
            <p className="mt-6 text-[#475569] leading-relaxed text-lg">
              {t("landing.bid.subtitle")}
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="border border-[#E2E8E4] p-4 md:p-5 rounded-md">
                <div className="nv-label">{t("landing.bid.steps")}</div>
                <div className="font-mono-nv text-xl md:text-2xl font-semibold text-[#0F4C3A] mt-2">CHF 100</div>
              </div>
              <div className="border border-[#E2E8E4] p-4 md:p-5 rounded-md">
                <div className="nv-label">{t("landing.bid.date_label")}</div>
                <div className="font-heading text-base md:text-lg font-medium text-[#0F4C3A] mt-2">{t("landing.bid.date")}</div>
              </div>
            </div>
          </div>
          <div
            className="h-80 md:h-[500px] rounded-md bg-cover bg-center"
            style={{ backgroundImage: `url(${ZURICH_IMG})` }}
          />
        </div>
      </section>

      {/* Interest Form (Lead) */}
      <section id="interest" className="bg-[#F8FAF9] border-y border-[#E2E8E4] scroll-mt-20">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <Pill>NIVARA · Membership</Pill>
            <h2 className="font-heading mt-6 text-3xl md:text-5xl font-medium tracking-tight text-[#0F4C3A] leading-[1.05]">
              {t("interest.title")}
            </h2>
            <p className="mt-3 font-heading italic text-lg md:text-xl text-[#475569]">
              {t("interest.subtitle")}
            </p>
            <p className="mt-8 text-lg text-[#0F4C3A] font-heading">
              {t("interest.tagline")}
            </p>
            <div className="mt-8 space-y-4 text-left text-[#475569] leading-relaxed text-base md:text-[1.05rem]">
              <p>{t("interest.intro_1")}</p>
              <p>{t("interest.intro_2")}</p>
              <p>{t("interest.intro_3")}</p>
              <p>{t("interest.intro_4")}</p>
            </div>
          </div>
          <div className="mt-12">
            <InterestForm />
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/login"
              data-testid="interest-login-link"
              className="text-sm text-[#0F4C3A] underline underline-offset-4 hover:text-[#D4AF37]"
            >
              {t("landing.cta.login")}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E8E4] py-10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-[#94A3B8]">
            {t("landing.footer.copyright")} · {t("brand.locations")}
          </div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#94A3B8]">
            found.nivaragroup.ch
          </div>
        </div>
      </footer>
    </div>
  );
}
